import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';

export default function ThesisLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.loginAuthor(formData);
      if (response.success && response.user) {
        authStorage.setAuthorAuth(response.user);
        toast.success('Добре дошли!');
        navigate('/thesis');
      } else {
        setError('Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Невалиден email или парола');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 via-amber-50/40 to-teal-50 dark:from-slate-950 dark:via-slate-900 dark:to-teal-950 flex items-center justify-center p-4 thesis-brand">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left: brand + tagline */}
        <div className="hidden lg:block px-6">
          <Link to="/thesis" className="inline-flex items-center gap-3 mb-8" data-testid="thesis-logo">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-700 to-amber-600 flex items-center justify-center shadow-lg">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="text-2xl font-serif font-bold tracking-tight">ThesisRAG</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Doctoral Writing Suite</div>
            </div>
          </Link>
          <h1 className="text-5xl font-serif font-bold leading-tight mb-4 text-foreground">
            Дисертационни глави,<br />
            <span className="text-teal-700 dark:text-teal-400">обосновани</span> на доказателства.
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed mb-6">
            ThesisRAG комбинира PubMed литература, ChromaDB векторно търсене и
            GPT-5.2 за писане на докторски дисертации без халюцинации — всеки
            цитат проверим през DOI или PMID в Manubot формат.
          </p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><span className="text-teal-700 dark:text-teal-400 font-bold">•</span> Семантично търсене с PubMedBERT embeddings</li>
            <li className="flex items-start gap-2"><span className="text-teal-700 dark:text-teal-400 font-bold">•</span> Експорт в Markdown / HTML / DOCX</li>
            <li className="flex items-start gap-2"><span className="text-teal-700 dark:text-teal-400 font-bold">•</span> Manubot цитат-синтаксис ([@doi:...], [@pmid:...])</li>
          </ul>
        </div>

        {/* Right: login card */}
        <Card className="border-stone-200 dark:border-slate-800 shadow-xl">
          <CardHeader className="text-center pt-8">
            <Link to="/thesis" className="lg:hidden flex justify-center mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-700 to-amber-600 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
            </Link>
            <CardTitle className="text-2xl font-serif">Вход в ThesisRAG</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Достъп до вашите дисертационни проекти
            </p>
          </CardHeader>
          <CardContent className="pb-8">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="thesis-login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="thesis-login-email"
                />
              </div>
              <div>
                <Label htmlFor="password">Парола</Label>
                <Input id="password" type="password" required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="thesis-login-password"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50 p-3 rounded" data-testid="thesis-login-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <Button type="submit" className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                disabled={loading} data-testid="thesis-login-submit">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Влез
              </Button>
              <p className="text-xs text-center text-muted-foreground pt-2">
                Все още нямате акаунт?{' '}
                <Link to="/author/register" className="underline hover:text-teal-700 dark:hover:text-teal-400">
                  Регистрирайте се
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
